import React, { useEffect, useRef } from 'react'
import * as echarts from 'echarts'
import styled, { useTheme } from 'styled-components'
import { formatMetricValue } from '../utils/zoningHelpers'

const ProgressBarWrapper = styled.div`
  width: 100%;
  padding: ${({ theme }) => `${theme.spacing.small} 0`};
`

const ChartContainer = styled.div`
  width: 100%;
  height: 8px;
  position: relative;
`

export interface MetricProgressBarTranslations {
  currentSituation?: string
  used?: string
  authorized?: string
  utilization?: string
  remainingPotential?: string
  available?: string
}

const DEFAULT_PROGRESS_BAR_TRANSLATIONS: MetricProgressBarTranslations = {
  currentSituation: 'Current situation',
  used: 'Used',
  authorized: 'Authorized',
  utilization: 'Utilization',
  remainingPotential: 'Remaining Potential',
  available: 'Available',
}

export interface MetricProgressBarProps {
  currentValue?: number
  authorizedValue?: number
  unit: string
  height?: number
  showLabels?: boolean
  translations?: MetricProgressBarTranslations
}

/**
 * Minimalist horizontal progress bar showing current (blue) vs authorized (green) area
 * Uses ECharts for smooth rendering and interactions
 */
export const MetricProgressBar: React.FC<MetricProgressBarProps> = ({
  currentValue,
  authorizedValue,
  unit,
  height = 8,
  showLabels = false,
  translations: translationsProp,
}) => {
  const t = { ...DEFAULT_PROGRESS_BAR_TRANSLATIONS, ...translationsProp }
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)
  const theme = useTheme()

  useEffect(() => {
    if (
      !chartRef.current ||
      currentValue === undefined ||
      authorizedValue === undefined
    ) {
      return
    }

    // Initialize chart
    if (chartInstance.current) {
      chartInstance.current.dispose()
    }
    chartInstance.current = echarts.init(chartRef.current)

    // Calculate segments
    const maxValue = Math.max(currentValue, authorizedValue)
    const currentPercentage = (currentValue / maxValue) * 100
    const remainingValue = Math.max(0, authorizedValue - currentValue)
    const remainingPercentage = (remainingValue / maxValue) * 100
    const utilization =
      authorizedValue > 0 ? (currentValue / authorizedValue) * 100 : 0

    // Determine colors
    const currentColor =
      currentValue > authorizedValue
        ? theme.colors.danger // Red if over-utilized
        : theme.colors.primary // Blue for normal

    const option: echarts.EChartsOption = {
      animation: true,
      animationDuration: 300,
      animationEasing: 'cubicOut',
      grid: {
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        max: maxValue,
        show: false,
      },
      yAxis: {
        type: 'category',
        data: [''],
        show: false,
      },
      tooltip: {
        trigger: 'item',
        confine: false, // Allow tooltip to overflow chart bounds
        position: 'right', // Position to the right of the cursor
        formatter: (params: any) => {
          const isCurrentBar = params.seriesName === 'current'
          if (isCurrentBar) {
            return `
              <div style="padding: 8px; min-width: 180px;">
                <div style="font-weight: 600; margin-bottom: 6px; color: ${theme.colors.text.primary}">${t.currentSituation}</div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="color: ${theme.colors.text.secondary}">${t.used}:</span>
                  <span style="font-weight: 500; color: ${currentColor}">${formatMetricValue(currentValue, unit)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
                  <span style="color: ${theme.colors.text.secondary}">${t.authorized}:</span>
                  <span style="font-weight: 500;">${formatMetricValue(authorizedValue, unit)}</span>
                </div>
                <div style="display: flex; justify-content: space-between; padding-top: 4px; border-top: 1px solid ${theme.colors.border.light};">
                  <span style="color: ${theme.colors.text.secondary}">${t.utilization}:</span>
                  <span style="font-weight: 600; color: ${theme.colors.primary}">${utilization.toFixed(0)}%</span>
                </div>
              </div>
            `
          } else {
            return `
              <div style="padding: 8px; min-width: 180px;">
                <div style="font-weight: 600; margin-bottom: 6px; color: ${theme.colors.text.primary}">${t.remainingPotential}</div>
                <div style="display: flex; justify-content: space-between;">
                  <span style="color: ${theme.colors.text.secondary}">${t.available}:</span>
                  <span style="font-weight: 500; color: ${theme.colors.success}">${formatMetricValue(remainingValue, unit)}</span>
                </div>
              </div>
            `
          }
        },
        backgroundColor: theme.colors.background.primary,
        borderColor: theme.colors.border.default,
        borderWidth: 1,
        textStyle: {
          color: theme.colors.text.primary,
          fontSize: 12,
        },
        extraCssText:
          'box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border-radius: 4px;',
      },
      series: [
        {
          name: 'current',
          type: 'bar',
          stack: 'total',
          data: [currentValue],
          barWidth: height,
          itemStyle: {
            color: currentColor,
            borderRadius: [4, 0, 0, 4],
          },
          emphasis: {
            itemStyle: {
              opacity: 0.8,
            },
          },
        },
        {
          name: 'remaining',
          type: 'bar',
          stack: 'total',
          data: [remainingValue],
          barWidth: height,
          itemStyle: {
            color: remainingValue > 0 ? theme.colors.success : 'transparent',
            borderRadius: [0, 4, 4, 0],
          },
          emphasis: {
            itemStyle: {
              opacity: 0.8,
            },
          },
        },
      ],
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [currentValue, authorizedValue, unit, height, theme, t])

  // Don't render if values are missing
  if (currentValue === undefined || authorizedValue === undefined) {
    return null
  }

  return (
    <ProgressBarWrapper>
      <ChartContainer ref={chartRef} style={{ height: `${height}px` }} />
    </ProgressBarWrapper>
  )
}
